/**
 * ThyroDoctorMascot.js
 *
 * The ThyroConsult "happy thyroid doctor" mark (finalised 4 Oct 2026,
 * design "D2 revised"): the honeycomb thyroid from favicon.svg, with
 * blinking eyes and eyebrows, ears, a stethoscope running from the ears,
 * and the bell held in the mascot's right hand.
 *
 * Rolled out first on LandingPage.js and RegisterPage.js only. The shared
 * <Logo /> in common/index.js is deliberately unchanged until the owner
 * approves the look — then <MascotLogo /> below can replace it everywhere.
 *
 *   <ThyroDoctorMascot height={180} />          // mark only
 *   <MascotLogo height={40} />                  // mark + "ThyroConsult™" wordmark
 *
 * The blink stops for visitors whose device asks for reduced motion.
 */

import React, { useEffect, useId } from 'react';

const OUTLINE = 'M93,125 L107,125 C112,142 122,162 138,168 C156,177 176,170 185,150 C194,129 192,102 180,80 C171,63 170,48 159,37 C148,26 129,27 119,43 C112,70 109,85 107,104 L93,104 C91,85 88,70 81,43 C71,27 52,26 41,37 C30,48 29,63 20,80 C8,102 6,129 15,150 C24,170 44,177 62,168 C78,162 88,142 93,125 Z';

// viewBox is wider than tall: width = height × 308 / 252
const VB_W = 308;
const VB_H = 252;

const INK = '#1e1e1e';
const RED = '#E0475F';
const HONEYCOMB = '#F7B3C0';
const STEEL = '#6F7F86';
const SILVER = '#C9D2D6';
const CHEEK = '#B8233D';

let stylesInjected = false;
function injectStyles() {
  if (stylesInjected || typeof document === 'undefined') return;
  stylesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    .tc-mascot-blink { transform-box: fill-box; transform-origin: center; animation: tcMascotBlink 4.2s infinite; }
    @keyframes tcMascotBlink { 0%, 90%, 100% { transform: scaleY(1); } 94% { transform: scaleY(0.1); } }
    @media (prefers-reduced-motion: reduce) { .tc-mascot-blink { animation: none; } }
  `;
  document.head.appendChild(style);
}

const Eye = ({ cx }) => (
  <g className="tc-mascot-blink">
    <ellipse cx={cx} cy="108" rx="15" ry="18" fill="#FFFFFF" stroke={INK} strokeWidth="3.5" />
    <ellipse cx={cx + 2} cy="111" rx="8.5" ry="10" fill={INK} />
    <circle cx={cx + 4.5} cy="107" r="2.6" fill="#FFFFFF" />
  </g>
);

export default function ThyroDoctorMascot({ height = 120, title = 'ThyroConsult', style }) {
  useEffect(() => { injectStyles(); }, []);
  // Each mascot on a page needs its own pattern id (useId gives ":r0:" —
  // colons removed so url(#…) resolves in every browser).
  const patternId = 'tcHoney' + useId().replace(/:/g, '');
  const width = Math.round((height * VB_W) / VB_H);

  return (
    <svg
      viewBox={`-66 18 ${VB_W} ${VB_H}`}
      width={width}
      height={height}
      role={title ? 'img' : undefined}
      aria-label={title || undefined}
      aria-hidden={title ? undefined : true}
      style={{ display: 'block', flexShrink: 0, ...style }}
    >
      <defs>
        <pattern id={patternId} patternUnits="userSpaceOnUse" x="3" y="2" width="17.32" height="30">
          <polygon points="17.32,5 17.32,15 8.66,20 0,15 0,5 8.66,0" fill="none" stroke={HONEYCOMB} strokeWidth="2.6" />
          <line x1="8.66" y1="20" x2="8.66" y2="30" stroke={HONEYCOMB} strokeWidth="2.6" />
        </pattern>
      </defs>

      {/* Ears */}
      <path d="M14,84 C-12,80 -12,120 11,118" fill={RED} stroke={INK} strokeWidth="8" strokeLinecap="round" />
      <path d="M186,84 C212,80 212,120 189,118" fill={RED} stroke={INK} strokeWidth="8" strokeLinecap="round" />

      {/* Stethoscope: ear tips → joint below the thyroid → bell */}
      <path d="M-4,101 C-26,150 10,212 100,214 M204,101 C226,150 190,212 100,214" fill="none" stroke={STEEL} strokeWidth="5.5" strokeLinecap="round" />
      <circle cx="-3" cy="101" r="6.5" fill={INK} />
      <circle cx="203" cy="101" r="6.5" fill={INK} />
      <circle cx="100" cy="214" r="6.5" fill={STEEL} />
      <path d="M100,214 C66,246 -50,246 -40,192" fill="none" stroke={STEEL} strokeWidth="5.5" strokeLinecap="round" />

      {/* Thyroid: red fill, honeycomb, black outline (same as favicon.svg) */}
      <path fill={RED} d={OUTLINE} />
      <path fill={`url(#${patternId})`} d={OUTLINE} />
      <path fill="none" stroke={INK} strokeWidth="16" strokeLinecap="round" strokeLinejoin="round" d={OUTLINE} />

      {/* Right arm (viewer's left) holding the bell */}
      <path d="M14,146 C4,160 -10,168 -28,174" fill="none" stroke={INK} strokeWidth="9" strokeLinecap="round" />
      <circle cx="-28" cy="174" r="8.5" fill={INK} />
      <circle cx="-40" cy="170" r="22" fill={SILVER} stroke={INK} strokeWidth="5" />
      <circle cx="-40" cy="170" r="12" fill="#FFFFFF" stroke={STEEL} strokeWidth="3" />
      <circle cx="-40" cy="170" r="5" fill={STEEL} />

      {/* Left arm (viewer's right), waving */}
      <path d="M186,146 C202,138 214,120 218,100" fill="none" stroke={INK} strokeWidth="9" strokeLinecap="round" />
      <circle cx="219" cy="95" r="8.5" fill={INK} />

      {/* Eyes and eyebrows, each pair tilted outward */}
      <g transform="rotate(-15 64 100)">
        <path transform="rotate(10 64 67)" d="M49,74.5 Q64,56.5 78,70.8 Q64,70.5 49,74.5 Z" fill={INK} stroke={INK} strokeWidth="3" strokeLinejoin="round" />
        <Eye cx={64} />
      </g>
      <g transform="rotate(15 136 100)">
        <path transform="rotate(-10 136 67)" d="M122,70.8 Q136,56.5 151,74.5 Q136,70.5 122,70.8 Z" fill={INK} stroke={INK} strokeWidth="3" strokeLinejoin="round" />
        <Eye cx={136} />
      </g>

      {/* Cheeks */}
      <ellipse cx="32" cy="138" rx="10" ry="7" fill={CHEEK} opacity="0.55" />
      <ellipse cx="168" cy="138" rx="10" ry="7" fill={CHEEK} opacity="0.55" />
    </svg>
  );
}

// Brand text, three rows (6 Oct 2026) — used by every logo on the site:
//   ThyroConsult™ by                 (row 1)
//   INDIA THYROID CLINIC™            (row 2, same size)
//   Online Thyroid Opinion Platform  (row 3, smaller)
// On a narrow phone screen rows 1–2 shrink (never below 13px) so the
// header never runs off the screen.
const TM = ({ color }) => (
  <sup style={{ fontSize: '0.5em', fontWeight: 700, color, verticalAlign: 'super', marginLeft: 1 }}>TM</sup>
);
// taglineColor: row 3 in dark black (6 Oct 2026); a light colour on dark backgrounds.
export const BrandText = ({ fontSize = 18, color = 'var(--teal-600)', subColor = 'var(--text-secondary)', taglineColor = '#111111', fontFamily = 'var(--font-display)', align = 'left' }) => {
  const size = `clamp(13px, 3.6vw, ${fontSize}px)`;
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: align === 'center' ? 'center' : 'flex-start', textAlign: align, lineHeight: 1.15, minWidth: 0 }}>
      <span style={{ fontFamily, fontWeight: 700, fontSize: size, color, whiteSpace: 'nowrap' }}>
        ThyroConsult<TM color={subColor} /><span style={{ fontWeight: 400 }}> by</span>
      </span>
      <span style={{ fontFamily, fontWeight: 700, fontSize: size, color, whiteSpace: 'nowrap' }}>
        INDIA THYROID CLINIC<TM color={subColor} />
      </span>
      <span style={{ fontFamily: 'var(--font-body)', fontWeight: 400, fontSize: `calc(${size} * 0.62)`, color: taglineColor, marginTop: 2, whiteSpace: 'nowrap' }}>
        Online Thyroid Opinion Platform
      </span>
    </span>
  );
};

// Mark + brand text, laid out like the <Logo /> in common/index.js.
export const MascotLogo = ({ height = 48, fontSize = 18, gap = 10 }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap, minWidth: 0 }}>
    <ThyroDoctorMascot height={height} title="" />
    <BrandText fontSize={fontSize} />
  </div>
);
